import { rgba } from 'polished';
import { FocusEvent, KeyboardEventHandler, useLayoutEffect, useRef } from 'react';
import styled from 'styled-components';

import { tokens } from '../theme/tokens';

const Editable = styled.div`
	&:empty::before {
		content: attr(data-placeholder);
		color: ${rgba(tokens.text.secondary, 0.3)};
		pointer-events: none;
	}
`;

export function PlainInput(props: {
	autoFocus?: boolean;
	selectOnFocus?: boolean;
	multiline?: boolean;
	sync?: boolean;
	readOnly?: boolean;
	value?: string;
	placeholder?: string;
	label?: string;
	onChange?: (text: string) => void;
	onFocus?: (event: FocusEvent<HTMLDivElement>) => void;
	onBlur?: () => void;
	onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const mounted = useRef(false);

	const isCurrentRef = Boolean(ref.current);

	useLayoutEffect(() => {
		if (ref.current && props.autoFocus && mounted.current === false) {
			mounted.current = true;

			if (!props.sync) {
				ref.current.focus();

				if (props.selectOnFocus) {
					setTimeout(() => {
						document.execCommand('selectAll', false, undefined);
					});
				}

				return;
			}

			const selection = document.getSelection();
			const existing = selection?.getRangeAt(0);

			if (existing) {
				try {
					const range = document.createRange();
					range.setStart(ref.current.childNodes[0], existing.startOffset);
					range.setEnd(ref.current.childNodes[0], existing.endOffset);

					selection?.removeAllRanges();
					selection?.addRange(range);
				} catch {
					ref.current.focus();
				}
			}
		}
	}, [isCurrentRef]);

	useLayoutEffect(() => {
		if (ref.current?.innerText !== props.value) {
			ref.current!.innerText = props.value ?? '';
		}
	}, [isCurrentRef, props.value]);

	return (
		<Editable
			ref={ref}
			role="button"
			aria-label={props.label ?? props.placeholder}
			contentEditable={props.readOnly ? false : 'plaintext-only'}
			suppressContentEditableWarning
			data-placeholder={props.placeholder}
			onInput={(event) => props.onChange?.(event.currentTarget.innerText)}
			tabIndex={-1}
			onFocus={props.onFocus}
			onKeyDown={
				props.onKeyDown ??
				((event) => {
					if (event.key === 'Enter' && props.multiline === false) {
						event.preventDefault();
						return false;
					}
				})
			}
			onBlur={props.onBlur}
		/>
	);
}
